import { useSearchParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { CheckCircle2, CreditCard, MessageCircle } from 'lucide-react'
import { toast } from 'sonner'

const PLAN_CONFIG = {
  MONTHLY_STANDARD: {
    label: 'Standard (Mensuel)',
    amount: '30 000 CFA',
  },
  YEARLY_ECO: {
    label: 'Eco (Annuel)',
    amount: '300 000 CFA',
  },
  LIFETIME_PREMIUM: {
    label: 'Premium (Annuel)',
    amount: '600 000 CFA',
  },
} as const

export default function ManualPaymentPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()

  const plan = params.get('plan') as keyof typeof PLAN_CONFIG | null

  const planInfo = plan ? PLAN_CONFIG[plan] : null

  const WHATSAPP_NUMBER = '2250709149747' // adapte
  const whatsappMessage = encodeURIComponent(
    `Bonjour, j’ai effectué un paiement pour le plan ${planInfo?.label}. Voici ma preuve.`
  )

  return (
    <div className='min-h-dvh bg-background px-4 py-10'>
      <div className='mx-auto max-w-2xl'>
        {/* HEADER */}
        <div className='text-center'>
          <Badge variant='secondary'>Activation du plan</Badge>

          <h1 className='mt-4 text-3xl font-extrabold tracking-tight'>
            Finalisez votre abonnement
          </h1>

          <p className='mt-3 text-muted-foreground'>
            Votre demande a été enregistrée. Il reste une dernière étape pour
            activer votre plan.
          </p>
        </div>

        {/* PLAN INFO */}
        <Card className='mt-6 rounded-2xl'>
          <CardHeader>
            <CardTitle className='flex items-center justify-between'>
              <span>Plan sélectionné</span>
              <Badge>{planInfo?.label}</Badge>
            </CardTitle>
          </CardHeader>

          <CardContent>
            <div className='text-2xl font-bold'>{planInfo?.amount}</div>
          </CardContent>
        </Card>

        {/* INSTRUCTIONS */}
        <Card className='mt-6 rounded-2xl border shadow-sm'>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <CreditCard className='h-5 w-5 text-primary' />
              Instructions de paiement
            </CardTitle>
          </CardHeader>

          <CardContent className='space-y-4 text-sm'>
            <p className='text-muted-foreground'>
              En attendant l’automatisation complète des paiements, merci de
              suivre les étapes ci-dessous :
            </p>

            <ul className='space-y-3'>
              <li className='flex gap-2'>
                <CheckCircle2 className='h-4 w-4 text-primary mt-1' />
                <span>Effectuez le paiement via Wave ou Mobile Money</span>
              </li>

              <li className='flex gap-2'>
                <CheckCircle2 className='h-4 w-4 text-primary mt-1' />
                <span>
                  Montant : <strong>{planInfo?.amount}</strong>
                </span>
              </li>

              <li className='flex gap-2'>
                <CheckCircle2 className='h-4 w-4 text-primary mt-1' />
                <span>Envoyez la preuve de paiement via WhatsApp</span>
              </li>
            </ul>

            <div className='rounded-xl border bg-muted/30 p-4 text-xs'>
              Nous nous excusons pour cette étape supplémentaire. Cette solution
              est temporaire, en attendant l’intégration complète des paiements
              automatiques.
            </div>
          </CardContent>
        </Card>

        {/* ACTIONS */}
        <div className='mt-6 grid gap-3'>
          {/* WhatsApp */}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            <Button className='w-full' size='lg'>
              <MessageCircle className='mr-2 h-4 w-4' />
              Envoyer ma preuve sur WhatsApp
            </Button>
          </a>

          {/* Dashboard */}
          <Button
            variant='outline'
            size='lg'
            onClick={() => navigate('/dashboard')}
          >
            Retour au dashboard
          </Button>
        </div>

        {/* FALLBACK */}
        <Card className='mt-6 rounded-2xl border border-dashed'>
          <CardHeader>
            <CardTitle className='text-sm'>Problème avec WhatsApp ?</CardTitle>
          </CardHeader>

          <CardContent className='space-y-3 text-sm'>
            <p className='text-muted-foreground'>
              Si le bouton ne fonctionne pas, vous pouvez envoyer votre preuve
              manuellement :
            </p>

            {/* Numéro */}
            <div className='flex items-center justify-between rounded-lg border p-3'>
              <div>
                <p className='text-xs text-muted-foreground'>Numéro WhatsApp</p>
                <p className='font-medium'>+225 07 09 14 97 47</p>
              </div>

              <Button
                size='sm'
                variant='outline'
                onClick={() => {
                  navigator.clipboard.writeText('+2250709149747')
                  toast.success('Numéro copié')
                }}
              >
                Copier
              </Button>
            </div>

            {/* Message prêt */}
            <div className='rounded-lg border p-3'>
              <p className='text-xs text-muted-foreground mb-1'>
                Message à envoyer
              </p>
              <p className='text-xs'>
                Bonjour, j’ai effectué le paiement pour le plan{' '}
                {planInfo?.label}. Voici ma preuve.
              </p>
            </div>

            <p className='text-xs text-muted-foreground'>
              Vous pouvez aussi nous contacter directement si besoin.
            </p>
          </CardContent>
        </Card>

        <Separator className='my-6' />

        {/* FOOTER INFO */}
        <p className='text-center text-xs text-muted-foreground'>
          L’activation se fait généralement en quelques minutes après
          vérification.
        </p>
      </div>
    </div>
  )
}
